import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import LoadingDataSpinner from 'component/loading_spinner/loading_data_spinner';
import config from 'config';
import { GET_STORE_BY_DOMAIN } from 'graph_ql/store/queries';
import { GET_SUGGESTED_QUESTIONS_BY_IDS } from 'graph_ql/suggested_questions/queries';
import { useUrlQuery } from 'hooks/useUrlQuery';
import StoreNotApprovedPage from 'pages/StoreNotApprovedPage';
import { ReactChild, ReactChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { globalActions } from 'redux/global/global.slice';
import { RootState } from 'redux/store';
import { setIsAppInWidgetMode, setWidgetStore } from 'redux/widget/widget.slice';
import { SESSION_STORAGE_KEY } from 'utils/constant';
import utils, { getClientIP, getStoreDevAttributes, isValidIp } from 'utils/utils';
import { SEARCH_STYLE, SEARCH_STYLE_TYPE } from 'graph_ql/search/search_style';
import { buildStoreFilter, constructItemUrls, generateItemUrlWithAffiliateCode } from '../utils/getQuerySearchStyles';
import uniq from 'lodash/uniq';
import isEqual from 'lodash/isEqual';
import { TimedSessionStorage } from 'utils/TimedSessionStorage';
import firstProcessOnboardingContainer from 'assets/img/onboarding_bg_1.png';
import useConfigurationData from '../hooks/useConfigurationData';
import { DynamicLayoutVersion } from '../types/types';
import { setConfiguration } from 'features/configuration/slice';
import { useQueryParams } from '../hooks/useQueryParams';
import { DYNAMIC_LAYOUT_PARAM } from '../constants';
import { GET_RECOMMENDATIONS } from 'graph_ql/styles/queries';
import { AlgoliaInput, AlgoliaResponse } from 'graph_ql/types/types';
import { getItem_getItem_data } from '/../__generated__/getItem';
import { GET_ITEMS_BY_ID } from 'graph_ql/item/queries';
import styleItemUtils from 'utils/style_item';
import { PATH } from './constant';
import { useLocation } from 'react-router-dom';
import { StorePlatform } from '../containers/create-style/AddOrSelectItem/type';
import i18next from 'i18next';
import { StyleStatus } from '__generated__/globalTypes';

const host = window.location.hostname;
const isLocal = host.includes('localhost') || isValidIp(host);
interface IStoreProvider {
    children: ReactChild | ReactChildren;
}
const appBaseUrl = process.env.REACT_APP_APP_ORIGIN;

interface IListingPath {
    path: string;
    container: string;
    productCardSelector?: string;
    productRowSelector?: string;
}

export enum DisplayTitle {
    STYLE_TITLE = 'STYLE_TITLE',
    ITEM_TITLE = 'ITEM_TITLE',
}

const NUMBER_OF_PURPOSE_SEASON = 4;
const NUMBER_OF_RECOMMEND_STYLES = 5;

export default function StoreProvider({ children }: IStoreProvider) {
    const location = useLocation();
    const configurationData = useConfigurationData();
    const queryParams = useQueryParams();
    const dynamicLayoutVersionParam = queryParams.get(DYNAMIC_LAYOUT_PARAM);
    const isStoreApproved = useSelector((state: RootState) => state.widget.store?.approved);
    const widgetStore = useSelector((state: RootState) => state.widget.store);
    const sourceUrl = useSelector((state: RootState) => state.widget.sourceUrl);
    const isPdp = useSelector((state: RootState) => state.widget.isPdp);
    const productDetails = useSelector((state: RootState) => state.widget.productDetails);
    const isWidget = useMemo(() => window.top !== window.self, []);
    const dispatch = useDispatch();
    const searchQuery = useUrlQuery();
    const listingStyleContainerRef = useRef(widgetStore?.listingStyleContainers);
    const listingStylePathRef = useRef<null | IListingPath>(null);
    const client = useApolloClient();
    const [triggeredGetRecommendationForUrl, setTriggeredGetRecommendationForUrl] = useState<string | null>(null);
    const [shouldMergeStyles, setShouldMergeStyles] = useState(false);
    const [storeReady, setStoreReady] = useState(false);

    const [getItem] = useLazyQuery<AlgoliaResponse<getItem_getItem_data, 'algolia_get_item_by_id'>, AlgoliaInput>(
        GET_ITEMS_BY_ID,
    );

    useEffect(() => {
        (async () => {
            console.log('%c widgetStore lang code', 'color: yellow;font-size:16px', widgetStore?.languageCode);
            console.log('%c Current language code', 'color: yellow;font-size:16px', i18next.languages);
            // Check if the widgetStore has a languageCode and change the i18next language if it is different from the current one
            if (widgetStore?.languageCode && widgetStore?.languageCode !== i18next.languages[0]) {
                await i18next.changeLanguage(widgetStore?.languageCode);
            }
        })();
    }, [widgetStore]);

    const isPublishedStyleStore = useMemo(() => {
        if (widgetStore?.allowedStyleStatus?.includes(StyleStatus.published)) {
            return true;
        }
        return false;
    }, [widgetStore?.allowedStyleStatus]);

    const getStoreAttributes = useCallback(
        (inputStore: any) => {
            console.log('getStoreAttributes');
            let store = { ...inputStore };
            if (!store?.taffiWidgetButtonText || !store?.taffiWidgetButtonImageURL) {
                try {
                    console.log('Merge store configuration with global store configuration');
                    const { storeConfig } = configurationData;
                    if (
                        !storeConfig ||
                        !storeConfig.defaultWidgetButtonText ||
                        !storeConfig.defaultWidgetButtonImageUrl
                    ) {
                        console.log(
                            'storeConfig or defaultWidgetButtonText or defaultWidgetButtonImageUrl is not available',
                        );
                        if (!store?.taffiWidgetButtonText) {
                            store.taffiWidgetButtonText = process.env.REACT_APP_TAFFI_WIDGET_BUTTON_TEXT;
                        }
                        if (!store?.taffiWidgetButtonImageURL) {
                            store.taffiWidgetButtonImageURL = process.env.REACT_APP_TAFFI_WIDGET_IMAGE_URL;
                        }
                    } else {
                        console.log('storeConfig', storeConfig);
                        if (!store?.taffiWidgetButtonText) {
                            store.taffiWidgetButtonText = storeConfig?.defaultWidgetButtonText;
                        }
                        if (!store?.taffiWidgetButtonImageURL) {
                            store.taffiWidgetButtonImageURL = storeConfig?.defaultWidgetButtonImageUrl;
                        }
                    }
                } catch (e) {
                    if (!store?.taffiWidgetButtonText) {
                        store.taffiWidgetButtonText = process.env.REACT_APP_TAFFI_WIDGET_BUTTON_TEXT;
                    }
                    if (!store?.taffiWidgetButtonImageURL) {
                        store.taffiWidgetButtonImageURL = process.env.REACT_APP_TAFFI_WIDGET_IMAGE_URL;
                    }
                    console.log('Error merging a store configuration with global store configuration', e);
                }
            }
            return {
                platform: store?.platform,
                languageCode: store?.languageCode || 'en',
                productPagePattern: store?.productPagePattern,
                productPageElementSelectorPattern: store?.productPageElementSelectorPattern,
                displayWidgetURLs: store?.displayWidgetURLs ?? [],
                hideWidgetURLs: store?.hideWidgetURLs ?? [],
                showTaffiWidget: isLocal || (store?.approved && store?.showTaffiWidget),
                showFloatingButton: store?.showFloatingButton,
                showAskAmiraCardPDP: store?.showAskAmiraCardPDP,
                enableProductSpecificQuestion: store?.enableProductSpecificQuestion,
                plpURLParams: store?.plpURLParams,
                showDressItRightCardPDP: store?.showDressItRightCardPDP,
                stringTranslator: store?.stringTranslator,
                plpWidgetReplaceItemCard: store?.plpWidgetReplaceItemCard,
                displayTitlePDP: store?.displayTitlePDP,
                displayTitlePLP: store?.displayTitlePLP,
                isMessageFromTaffi: true,
                affiliateLink: store?.affiliateLink,
                defaultWidgetStateOpen: store?.defaultWidgetStateOpen,
                enableCustomWidgetScript: store?.enableCustomWidgetScript,
                customWidgetScript: store?.customWidgetScript,
                hideTaffiWidgetButtonImage: store?.hideTaffiWidgetButtonImage,
                taffiWidgetButtonText: store?.taffiWidgetButtonText,
                taffiWidgetButtonImageURL: store?.taffiWidgetButtonImageURL,
                popupQuestionsDelay: store?.popupQuestionsDelay,
                widgetCustomCss: store?.widgetCustomCss,
                taffiWidgetCollapsible: store?.taffiWidgetCollapsible,
                taffiWidgetCollapsibleDelay: store?.taffiWidgetCollapsibleDelay,
                taffiDynamicLayoutVersion: store?.styleLayoutVersion
                    ? store?.styleLayoutVersion
                    : configurationData.stitcher.dynamicLayoutVersion,
                enableSearchWidget: store?.enableSearchWidget,
                searchWidgetConfig: store?.searchWidgetConfig,
                searchWidgetResultConfig: store?.searchWidgetResultConfig,
                storeId: store?.id,
                productTargetGender: store?.productTargetGender,
                enableSingleProductStyling: store?.enableSingleProductStyling,
                enableEngagingSlider: store?.enableEngagingSlider,
                allowedStyleStatus: store?.allowedStyleStatus,
            };
        },
        [configurationData],
    );

    const getDynamicLayoutVersion = useCallback(
        (sourceUrl: string | null) => {
            if (sourceUrl) {
                const url = new URL(sourceUrl);
                const urlParams = new URLSearchParams(url.search);
                return urlParams.get(DYNAMIC_LAYOUT_PARAM);
            }
            return dynamicLayoutVersionParam;
        },
        [dynamicLayoutVersionParam],
    );

    const runQuery = useMemo(
        () => async (variables: any) => {
            const { data: response } = await client.query({
                query: SEARCH_STYLE,
                variables,
                fetchPolicy: 'network-only',
            });
            return response?.locationsInRadius?.hits ?? [];
        },
        [client],
    );

    const handlePdpStyles = (hits: any[]) => {
        let purposes: string[] = [];
        let seasons: string[] = [];
        if (hits.length) {
            // pick first 4 purposes and seasons from styles
            for (const style of hits) {
                purposes = [...purposes, ...style.purposes.map((p) => p.title)];
                seasons = [...seasons, ...style.seasons.map((s) => s.title)];
            }
            purposes = uniq(purposes);
            seasons = uniq(seasons);
            if (purposes.length) {
                purposes = purposes.slice(0, NUMBER_OF_PURPOSE_SEASON);
            }
            if (seasons.length) {
                seasons = seasons.slice(0, NUMBER_OF_PURPOSE_SEASON);
            }
        }
        let styles = hits;
        if (configurationData.stitcher.dynamicLayoutVersion === DynamicLayoutVersion.v1) {
            styles = styles.filter((style) => style.images?.length);
        }
        window.parent.postMessage(
            {
                styleItRightSlides: styles,
                shouldMergeStyles,
                styleWidgetPosition: widgetStore?.styleWidgetPosition,
                styleWidgetTargetElement: widgetStore?.styleWidgetTargetElement,
                alternativeStyleWidgetTargetElement: widgetStore?.alternativeStyleWidgetTargetElement,
                styleWidgetAutoPlayDelay: widgetStore?.styleWidgetAutoPlayDelay,
                purposes,
                seasons,
                dynamicLayoutVersion: configurationData.stitcher.dynamicLayoutVersion,
                numberOfRecommendStyles: NUMBER_OF_RECOMMEND_STYLES,
            },
            widgetStore?.url,
        );
    };

    const handlePLPStyles = (hits: any[]) => {
        try {
            const generatedUrls = constructItemUrls(productDetails?.productUrls, sourceUrl, widgetStore?.affiliateLink);
            if (configurationData.stitcher.dynamicLayoutVersion === DynamicLayoutVersion.v1) {
                let styles = hits?.length
                    ? utils.getStylesWithUniqItem(
                        hits,
                        generatedUrls?.itemUrls ?? [],
                        configurationData.stitcher.dynamicLayoutVersion,
                    )
                    : [];
                styles = styles.filter((style: any) => style.images?.length);
                window.parent.postMessage(
                    {
                        updateStyleCard: true,
                        styles,
                        containerName: listingStylePathRef?.current?.container,
                        affiliateLink: widgetStore?.affiliateLink,
                        numOfStoreItemsPerTaffiStyle: widgetStore?.numOfStoreItemsPerTaffiStyle,
                        amiraCardBg: `url(${appBaseUrl}${firstProcessOnboardingContainer})`,
                        scrollCountForAmiraCard: widgetStore?.scrollCountForAmiraCard,
                    },
                    widgetStore?.url,
                );
            } else {
                // get items from algolia and merge with styles
                const promises: any = [];
                for (const style of hits) {
                    const promise = new Promise(async (resolve) => {
                        try {
                            const itemCondition = style.items.reduce((condition: string, i: any) => {
                                condition += (condition === '' ? '' : ' OR ') + `objectID:${i.id}`;
                                return condition;
                            }, '');
                            const itemsResponse = await getItem({
                                variables: {
                                    filters: itemCondition,
                                },
                            });
                            const items = itemsResponse.data?.algolia_get_item_by_id.hits.map((data: any) => {
                                return {
                                    id: data.id,
                                    data,
                                };
                            });
                            resolve({
                                ...style,
                                items: items ? styleItemUtils.mergeItemArrayObjects(style.items, items) : style.items,
                            });
                        } catch (e: any) {
                            console.log('Error merge algolia items for style ', e);
                            resolve(style);
                        }
                    });
                    promises.push(promise);
                }
                Promise.all(promises)
                    .then((styleWithItems) => {
                        let styles = styleWithItems?.length
                            ? utils.getStylesWithUniqItem(styleWithItems, generatedUrls?.itemUrls ?? [])
                            : [];
                        let updatedStyles: any = [];
                        if (widgetStore.displayTitlePLP === DisplayTitle.ITEM_TITLE && styles.length) {
                            styles.forEach((style) => {
                                let updatedStyle = style;
                                style.items.forEach((item) => {
                                    if (
                                        item.data.url.includes(style.primaryProductUrl) ||
                                        item?.data?.alternativeUrls?.includes(style?.primaryProductUrl)
                                    ) {
                                        updatedStyle = { ...updatedStyle, title: item.data.title };
                                    }
                                });
                                updatedStyles = [...updatedStyles, updatedStyle];
                            });
                        } else {
                            updatedStyles = styles;
                        }
                        window.parent.postMessage(
                            {
                                updateStyleCard: true,
                                styles: updatedStyles,
                                containerName: listingStylePathRef?.current?.container,
                                affiliateLink: widgetStore?.affiliateLink,
                                numOfStoreItemsPerTaffiStyle: widgetStore?.numOfStoreItemsPerTaffiStyle,
                                amiraCardBg: `url(${appBaseUrl}${firstProcessOnboardingContainer})`,
                                scrollCountForAmiraCard: widgetStore?.scrollCountForAmiraCard,
                            },
                            widgetStore?.url,
                        );
                    })
                    .catch((e: any) => console.log('Error merge style items', e));
            }
        } catch (error) {
            console.error('handlePLPStyles error', error);
        }
    };

    const [getStyleContainItem] = useLazyQuery(SEARCH_STYLE, {
        onCompleted: (response) => {
            const { hits } = response.locationsInRadius;
            handlePdpStyles(hits);
        },
        onError() {
            console.log('get style contain item error');
        },
    });

    const [getRecommendations] = useMutation(GET_RECOMMENDATIONS, {
        context: {
            allowAnonymousAuth: true,
        },
        onCompleted: (response) => {
            const hits = response?.getRecommendations?.recommendations ?? [];
            handlePdpStyles(hits);
            if (NUMBER_OF_RECOMMEND_STYLES > 1 && !shouldMergeStyles && !isPublishedStyleStore) {
                setShouldMergeStyles(true);
                const urlObject = new URL(sourceUrl);
                const urlWithAffiliateCode = generateItemUrlWithAffiliateCode(
                    urlObject.origin + urlObject.pathname,
                    widgetStore?.affiliateLink,
                );
                getRecommendations({
                    variables: {
                        input: {
                            item_url: urlWithAffiliateCode,
                            size: NUMBER_OF_RECOMMEND_STYLES,
                            store_id: widgetStore.id,
                            include_existing: true,
                            allowedStyleStatus: widgetStore?.allowedStyleStatus,
                        },
                    },
                });
            }
        },
        onError() {
            console.log('get style contain item error');
        },
    });

    const [getPLPRecommendations] = useMutation(GET_RECOMMENDATIONS, {
        context: {
            allowAnonymousAuth: true,
        },
        onError() {
            console.log('get style contain item error');
        },
    });

    useEffect(() => {
        if (!isEqual(listingStyleContainerRef.current, widgetStore?.listingStyleContainers)) {
            listingStyleContainerRef.current = widgetStore?.listingStyleContainers;
        }
    }, [widgetStore?.listingStyleContainers]);

    useEffect(() => {
        if (
            triggeredGetRecommendationForUrl !== sourceUrl &&
            sourceUrl &&
            widgetStore &&
            !isPdp &&
            widgetStore?.url &&
            widgetStore?.approved &&
            widgetStore?.enableListingStyles &&
            listingStyleContainerRef.current
        ) {
            setTriggeredGetRecommendationForUrl(sourceUrl);
            let currentPath = listingStyleContainerRef.current?.filter((item: any) => {
                return sourceUrl?.includes(item?.path);
            })[0];
            listingStylePathRef.current = currentPath ?? null;
            if (currentPath) {
                window.parent.postMessage(
                    {
                        requestProductUrl: true,
                        plpWidgetReplaceItemCard: widgetStore?.plpWidgetReplaceItemCard,
                        containerName: listingStylePathRef?.current?.container,
                        productCardSelector: listingStylePathRef?.current?.productCardSelector,
                        productRowSelector: listingStylePathRef?.current?.productRowSelector,
                        amiraCardBg: `url(${appBaseUrl}${firstProcessOnboardingContainer})`,
                        styleLayoutVersion: widgetStore?.styleLayoutVersion
                            ? widgetStore?.styleLayoutVersion
                            : configurationData.stitcher.dynamicLayoutVersion,
                        enableSingleProductStyling: widgetStore?.enableSingleProductStyling,
                        plpURLParams: widgetStore?.plpURLParams,
                    },
                    widgetStore?.url,
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sourceUrl,
        widgetStore?.url,
        widgetStore?.approved,
        widgetStore?.enableListingStyles,
        widgetStore?.styleLayoutVersion,
        configurationData,
        widgetStore,
        isPdp,
    ]);

    useEffect(() => {
        if (productDetails?.productUrls?.length && sourceUrl) {
            const generatedUrls = constructItemUrls(productDetails?.productUrls, sourceUrl, widgetStore?.affiliateLink);
            if (configurationData.stitcher.dynamicLayoutVersion === DynamicLayoutVersion.v1) {
                runQuery({
                    __typename: SEARCH_STYLE_TYPE.algolia_styles,
                    index: config.algolia.algolia_prefix + 'styles',
                    filters: `status:published AND (${generatedUrls.itemUrlForFilter})`,
                    hit: config.algoliaHitPerPage,
                    page: 0,
                    typoTolerance: false,
                })
                    .then((hits) => {
                        handlePLPStyles(hits);
                    })
                    .catch(() => {
                        console.debug('reponse SEARCH STYLE failed.');
                    });
            } else {
                const promises = generatedUrls.itemUrls.map((itemUrl: string) => {
                    return getPLPRecommendations({
                        variables: {
                            input: {
                                item_url: itemUrl,
                                size: 1,
                                store_id: widgetStore.id,
                                include_existing: true,
                                allowedStyleStatus: widgetStore?.allowedStyleStatus,
                            },
                        },
                    });
                });
                Promise.all(promises)
                    .then((response) => {
                        let hits = response.map((res, index: number) => {
                            const style = res?.data?.getRecommendations?.recommendations?.[0];
                            if (style) {
                                style.primaryProductUrl = generatedUrls.itemUrls[index];
                            }
                            return style;
                        });
                        hits = hits.filter((style: any) => !!style);
                        handlePLPStyles(hits);
                    })
                    .catch(() => {
                        console.debug('reponse GET_RECOMMENDATIONS failed.');
                    });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        productDetails,
        sourceUrl,
        widgetStore,
        widgetStore?.numOfStoreItemsPerTaffiStyle,
        widgetStore?.scrollCountForAmiraCard,
        widgetStore?.affiliateLink,
        widgetStore?.allowedStyleStatus,
        configurationData,
    ]);

    useEffect(() => {
        if (
            triggeredGetRecommendationForUrl !== sourceUrl &&
            sourceUrl &&
            widgetStore &&
            isPdp &&
            widgetStore?.approved &&
            widgetStore?.enableStyleWidget &&
            widgetStore?.styleWidgetPosition &&
            widgetStore?.styleWidgetTargetElement
        ) {
            setTriggeredGetRecommendationForUrl(sourceUrl);
            let urlObject = new URL(sourceUrl);
            if (widgetStore?.platform === StorePlatform.Vogacloset) {
                // remove country and language from sourceUrl
                urlObject = new URL(utils.removeCountryAndLanguageFromUrl(sourceUrl));
            }
            const urlWithAffiliateCode = generateItemUrlWithAffiliateCode(
                urlObject.origin + urlObject.pathname,
                widgetStore?.affiliateLink,
            );
            if (configurationData.stitcher.dynamicLayoutVersion === DynamicLayoutVersion.v2) {
                getRecommendations({
                    variables: {
                        input: {
                            item_url: urlWithAffiliateCode,
                            size: isPublishedStyleStore ? NUMBER_OF_RECOMMEND_STYLES : 1,
                            store_id: widgetStore.id,
                            include_existing: true,
                            allowedStyleStatus: widgetStore?.allowedStyleStatus,
                        },
                    },
                });
            } else {
                getStyleContainItem({
                    variables: {
                        __typename: SEARCH_STYLE_TYPE.algolia_styles,
                        index: config.algolia.algolia_prefix + 'styles',
                        query: `"${urlWithAffiliateCode}"`,
                        filters: `status:published AND (${buildStoreFilter(widgetStore)})`,
                        hit: config.algoliaHitPerPage,
                        page: 0,
                        typoTolerance: false,
                        advancedSyntax: true,
                    },
                });
            }
        }
        // eslint-disable-next-line
    }, [sourceUrl, widgetStore]);

    const overrideDynamicLayoutVersion = useCallback(
        (store: any) => {
            const urlDynamicLayoutVersion = getDynamicLayoutVersion(sourceUrl);
            if (urlDynamicLayoutVersion) {
                dispatch(
                    setConfiguration({
                        ...configurationData,
                        stitcher: {
                            ...configurationData.stitcher,
                            dynamicLayoutVersion: urlDynamicLayoutVersion,
                        },
                    }),
                );
            } else if (store.styleLayoutVersion) {
                TimedSessionStorage.setItemWithExpiration(SESSION_STORAGE_KEY.TAFFI_CONFIGURATION, {
                    ...configurationData,
                    stitcher: {
                        ...configurationData.stitcher,
                        dynamicLayoutVersion: store.styleLayoutVersion,
                    },
                });
                dispatch(
                    setConfiguration({
                        ...configurationData,
                        stitcher: {
                            ...configurationData.stitcher,
                            dynamicLayoutVersion: store.styleLayoutVersion,
                        },
                    }),
                );
            }
        },
        [configurationData, dispatch, getDynamicLayoutVersion, sourceUrl],
    );

    const [getStoreByDomain, { loading }] = useLazyQuery(GET_STORE_BY_DOMAIN, {
        async onCompleted(response) {
            let store = response?.getStoreByDomain;
            const storeDevAttributes = getStoreDevAttributes();
            if (storeDevAttributes) {
                store = { ...store, ...storeDevAttributes };
            }

            if (store && store.devAttributes && store.devIPs?.length) {
                const clientIP = await getClientIP();
                if (clientIP && store.devIPs.includes(clientIP)) {
                    store = { ...store, ...store.devAttributes };
                }
            }
            if (store) {
                overrideDynamicLayoutVersion(store);
            }
            TimedSessionStorage.setItemWithExpiration(SESSION_STORAGE_KEY.TAFFI_STORE_DETAILS, store, 5);
            setStoreReady(true);
            if (isWidget) {
                // To Hide the taffi widget if store is not approved
                const parentWindow = window.parent;
                parentWindow.postMessage(getStoreAttributes(store), store?.url);

                // Update redux slice for widget
                dispatch(setWidgetStore(store));
                if (location.pathname !== PATH.PDP_WIDGET) {
                    const questionIdSet = new Set<string>();
                    if (store?.suggestedQuestionIds?.length) {
                        store.suggestedQuestionIds.forEach((id: string) => questionIdSet.add(id));
                    }
                    if (store?.searchQuestionIds?.length) {
                        store.searchQuestionIds.forEach((id: string) => questionIdSet.add(id));
                    }
                    // get the ids from questionIdSet
                    const ids = Array.from(questionIdSet);
                    const questions = await getStoreSuggestedQuestions({
                        variables: {
                            input: { ids },
                        },
                    });
                    const suggestedQuestions: any[] = [];
                    const searchQuestions: any[] = [];
                    const results = questions.data?.getSuggestedQuestionsByIds;
                    if (results?.length) {
                        for (const question of results) {
                            if (store?.suggestedQuestionIds?.includes(question.id)) {
                                suggestedQuestions.push({
                                    id: question.id,
                                    value: question.value,
                                });
                            }
                            if (store?.searchQuestionIds?.includes(question.id)) {
                                searchQuestions.push({
                                    id: question.id,
                                    value: question.value,
                                });
                            }
                        }
                    }
                    parentWindow.postMessage(
                        {
                            isMessageFromTaffi: true,
                            popupQuestions: suggestedQuestions.map((q: any) => q?.value),
                            searchQuestions: searchQuestions,
                        },
                        store?.url,
                    );
                    TimedSessionStorage.setItemWithExpiration(
                        SESSION_STORAGE_KEY.TAFFI_STORE_SUGGESTED_QUESTIONS,
                        suggestedQuestions,
                    );
                }
            } else {
                dispatch(globalActions.setTaffiStoreId(store?.id));
                dispatch(globalActions.setTaffiEnableEngagingSlider(store?.enableEngagingSlider));
            }
        },
        onError(error) {
            console.error('Get Store by Domain Failed: ', error);
        },
    });

    useEffect(() => {
        if (!storeReady) {
            try {
                dispatch(setIsAppInWidgetMode(isWidget));
                const storeDevAttributes = getStoreDevAttributes();
                let store = TimedSessionStorage.getItemWithExpiration(SESSION_STORAGE_KEY.TAFFI_STORE_DETAILS);
                if (storeDevAttributes) {
                    store = { ...store, ...storeDevAttributes };
                }
                if (isWidget) {
                    if (store) {
                        dispatch(setWidgetStore(store));
                        if (configurationData) {
                            overrideDynamicLayoutVersion(store);
                            window.parent.postMessage(getStoreAttributes(store), store?.url);

                            const popupQuestions = TimedSessionStorage.getItemWithExpiration(
                                SESSION_STORAGE_KEY.TAFFI_STORE_SUGGESTED_QUESTIONS,
                            );
                            if (popupQuestions) {
                                window.parent.postMessage(
                                    {
                                        isMessageFromTaffi: true,
                                        popupQuestions: popupQuestions?.map((q) => q?.value),
                                    },
                                    store?.url,
                                );
                            }
                            setStoreReady(true);
                        }
                    } else {
                        let domain = searchQuery.get('shopUrl')!;
                        if (window?.location?.ancestorOrigins) {
                            const storeUrl = window?.location?.ancestorOrigins[0];
                            domain = new URL(storeUrl).hostname;
                        }
                        if (domain?.length) {
                            getStoreByDomain({
                                variables: {
                                    input: {
                                        domain,
                                    },
                                },
                            });
                        }
                    }
                } else {
                    if (store) {
                        dispatch(globalActions.setTaffiStoreId(store?.id));
                        dispatch(globalActions.setTaffiEnableEngagingSlider(store?.enableEngagingSlider));
                    } else {
                        getStoreByDomain({
                            variables: {
                                input: {
                                    domain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
                                },
                            },
                        });
                    }
                }
            } catch (error) {
                if (error instanceof SyntaxError) {
                    console.error('Invalid JSON:', error);
                } else console.log('Error Occured: ', error);
            }
        }
    }, [
        isWidget,
        sourceUrl,
        configurationData,
        dispatch,
        overrideDynamicLayoutVersion,
        getStoreAttributes,
        searchQuery,
        getStoreByDomain,
        storeReady,
    ]);

    const [getStoreSuggestedQuestions] = useLazyQuery(GET_SUGGESTED_QUESTIONS_BY_IDS, {
        onError(error) {
            console.error('Get Store suggested questions Failed: ', error);
        },
    });

    return isWidget ? (
        // Widget
        isLocal || isStoreApproved ? (
            <> {children} </>
        ) : loading ? (
            <LoadingDataSpinner />
        ) : (
            <StoreNotApprovedPage />
        )
    ) : // Non widget
        loading ? (
            <LoadingDataSpinner />
        ) : (
            <>{children}</>
        );
}
